import { Box, Flex, Loader, Transition } from '@mantine/core';
import { useNavigate } from 'react-router';
import { SiestaAction } from '../types';
import { AIActionSet } from '../sidebar/AIActionSet';
import { AISetTitle } from '../sidebar/AISetTitle';
import { SteeringFollowUpCard } from './SteeringFollowUpCard';
import { SteeringSuggestionCard } from './SteeringSuggestionCard';
import { useSteering } from './useSteering';

type Props = {
  suggestedActions: SiestaAction[];
  onActionClick: (action: SiestaAction) => void;
};

export function SteeringActionContent({ suggestedActions, onActionClick }: Props) {
  const navigate = useNavigate();
  const { state, isLoading, isWaitingForSuggestion, inFollowUp, handleSuggestionClick } =
    useSteering();

  function onSuggestionClick() {
    const href = handleSuggestionClick();
    if (href) navigate(href);
  }

  if (isLoading || isWaitingForSuggestion)
    return (
      <Flex align="center" justify="center" h="100%" w="100%">
        <Loader color="white" size="sm" />
      </Flex>
    );

  if (!state.response) {
    if (suggestedActions.length === 0) return null;
    return (
      <Flex flex={1} align="center" justify="center">
        <AIActionSet actions={suggestedActions} onActionClick={onActionClick} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100%" w="100%">
      <Box flex={1} style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        <Flex
          mih="100%"
          direction="column"
          align="center"
          justify={inFollowUp ? 'flex-start' : 'center'}
          gap="xxs"
          pt={inFollowUp ? 'xl' : 0}
          style={{ transition: 'padding-top 300ms ease-in-out' }}
        >
          <AISetTitle label="Siesta AI" />
          <SteeringSuggestionCard
            title={state.response.title}
            message={state.response.message}
            minimized={inFollowUp}
            onClick={onSuggestionClick}
          />
          <Transition mounted={inFollowUp && !!state.followUp} transition="fade-up" duration={300}>
            {styles => (
              <Flex flex={1} align="center" justify="center" w="100%" py="md" style={styles}>
                {state.followUp && <SteeringFollowUpCard message={state.followUp.message} />}
              </Flex>
            )}
          </Transition>
        </Flex>
      </Box>

      <Transition
        mounted={!inFollowUp && suggestedActions.length > 0}
        transition="slide-up"
        duration={300}
      >
        {styles => (
          <Flex mb="xxs" mt="xs" justify="center" style={styles}>
            <AIActionSet
              actions={suggestedActions}
              onActionClick={onActionClick}
              collapsible
              title="Suggested actions"
            />
          </Flex>
        )}
      </Transition>
    </Flex>
  );
}
